import { useState, KeyboardEvent, forwardRef, useImperativeHandle } from "react";
import { X } from "lucide-react";
import { Input } from "../../../../ui/input";

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
};

export type TagInputHandle = {
  /** Commit any pending text in the input as a tag. Returns the resulting tag list. */
  flush: () => string[];
};

export const TagInput = forwardRef<TagInputHandle, Props>(
  ({ value, onChange, suggestions = [], placeholder = "Type a tag and press Enter" }, ref) => {
    const [input, setInput] = useState("");

    const addTag = (raw: string): string[] => {
      const t = raw.trim().toLowerCase();
      if (!t) return value;
      if (value.includes(t)) {
        setInput("");
        return value;
      }
      const next = [...value, t];
      onChange(next);
      setInput("");
      return next;
    };

    const removeTag = (t: string) => onChange(value.filter((x) => x !== t));

    useImperativeHandle(ref, () => ({
      flush: () => (input.trim() ? addTag(input) : value),
    }), [input, value]);

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        // Stop the Enter from bubbling to dialog/form handlers and triggering
        // default-button submissions.
        e.preventDefault();
        e.stopPropagation();
        addTag(input);
      } else if (e.key === "Backspace" && !input && value.length) {
        removeTag(value[value.length - 1]);
      }
    };

    const filteredSuggestions = suggestions
      .filter((s) => !value.includes(s) && s.toLowerCase().includes(input.toLowerCase()))
      .slice(0, 8);

    return (
      <div>
        <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-input bg-background min-h-10">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-xs">
              {t}
              <button type="button" onClick={() => removeTag(t)} className="hover:text-destructive" aria-label={`Remove ${t}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => { if (input.trim()) addTag(input); }}
            placeholder={value.length ? "Add another tag…" : placeholder}
            className="border-0 shadow-none h-6 px-1 flex-1 min-w-32 focus-visible:ring-0"
          />
        </div>
        {input && filteredSuggestions.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {filteredSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                // Use onMouseDown so the suggestion is added before the input's
                // blur handler fires (which would otherwise commit the typed text).
                onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                className="text-xs px-2 py-0.5 rounded-full border border-border hover:bg-accent"
              >
                + {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);
TagInput.displayName = "TagInput";
